/** @format */

import { defineStore, StoreDefinition } from 'pinia';
// import { ElMessageBox } from 'element-plus';
import {
	Storage,
	param2Obj,
	setDocumentTheme,
	injectCssToDocument,
	injectJsToDocument,
	setDocumentFavicon,
} from '@power/utils';
import type { UserInfo, SubSystems, SubSystem, LoginParams, Visible } from '@/api/global';
import { store } from '@/store';
import {
	requestDataBeforeLogin,
	requestDataAfterLogin,
	getCasLoginInfo,
	getOauth2LoginInfo,
	login,
	logout,
	getAuthSilent2Config,
	getCommonjson,
	getMenu,
	findTreeDataWithNoRoot,
} from '@/api/global';
import { AuthTypeEnum } from '@/enums/authTypeEnum';
import {
	getFileUrlById,
	getFrontEndConfigItemByCodeAndItemCode,
	getEnvUrlByUrl,
	getSubSystemAliasFromLocationPathname,
	getHeaderType,
} from '@/utils/power';
import { ACCESS_TOKEN_KEY, REDIRECT_URI_KEY } from '@/enums/cacheEnum';
import { menuUtil } from '@/utils/menu';
import { initPDataEnv } from '@/utils/p-data';
import { useResetPasswordStore } from '@/store/modules/newPwd';

interface GlobalState {
	commonjson: any;
	stateLoading: string;
	baseInfo: any;
	frontEndConfig: any;
	authInfo: any;
	token: string;
	userInfo: UserInfo | null;
	subSystems: SubSystems;
	curSystem: SubSystem | undefined;
	curSystemName: string;
	curSystemNameDes: string;
	curSystemAlias: string;
	visible: Visible;
	urlGetSubSystem: string | null;
	isMobile: boolean;
	/**
	 * 1: 子系统收起展示
	 * 3: 子系统水平平铺展示
	 * 2: 子系统水平平铺展示 //已弃用 地质项目有用到这个值
	 *
	 * 默认当前系统可以通过更新服务器上文件内的配置即可实现头部子系统平铺展示。
	 */
	headerType: '1' | '2' | '3';
}

export const useGlobalStore: StoreDefinition = defineStore({
	id: 'runtime-global',
	state: (): GlobalState => ({
		commonjson: {
			env: {
				VITE_ENV_BASE_URL: null,
				VITE_APP_BASE_API: null,
				loginUri: '/login',
			},

			notredirect: ['/powerfactory/factory-render/'],
			'notredirect-des':
				'添加到 notredirect 里面关键字（应具有唯一性避免冲突）相关的地址可以直接访问, 不会被拦截跳转到其他页面',
			whitelist: ['/whitelist/'],
			'whitelist-des':
				'添加到 whitelist 里面关键字（应具有唯一性避免冲突）相关的地址可以不带token访问, 内部的接口也不应该需要token',
		},
		// 正在加载中
		stateLoading: 'init', // init 初始化， isLoading 正在加载， loaded 加载完成，
		baseInfo: {
			key: 'localData',
			key2: 'localData2',
			key3: 'localData3',
		},
		// 网站定制化基本信息数据
		frontEndConfig: [],
		// 网站定制化认证信息数据
		authInfo: {
			authType: 'native',
			casServerUrl: '',
			msg: '',
		},
		token: Storage.get(ACCESS_TOKEN_KEY),
		userInfo: null, // 用户数据
		subSystems: [], // 子系统数据
		curSystem: undefined, // 当前子系统
		curSystemName: '', // 当前子系统别名，已弃用，请使用curSystemAlias
		curSystemNameDes: 'curSystemName 为当前子系统别名，已弃用，请使用curSystemAlias', // 子系统别名，已弃用，请使用curSystemAlias
		curSystemAlias: '', // 当前子系统别名
		visible: {
			pcLayoutHeader: true, //显示pc-layout的头部
			pcLayoutMenu: true, //显示pc-layout的菜单
		},
		urlGetSubSystem: null, // 更新这个值可以自定义获取子系统的接口地址
		isMobile: false, // 默认false 如果是h5项目需要手动设置为true，移动端脚手架已经进行相关设置
		headerType: '1',
	}),
	getters: {
		getBaseInfo(): any {
			return this.baseInfo;
		},
	},
	actions: {
		setData(key, value) {
			this[key] = value;
		},
		setStateLoading(stateLoading) {
			this.stateLoading = stateLoading;
		},
		/**
		 * 保存服务器上的前端配置信息到 commonjson，
		 * 并且把 commonjson.env 里面的数据放到 window.pData.env 里面方便全局使用。
		 */
		setCommonjson(commonjson) {
			this.commonjson = commonjson;
			const env = commonjson.env;
			if (env) {
				initPDataEnv(env);
			}
		},
		setBaseInfo(baseInfo) {
			this.baseInfo = baseInfo;
		},
		// 更新从服务器获取登录前需要获取到的数据，包括前端配置数据
		setFrontEndConfig(frontEndConfig) {
			this.frontEndConfig = frontEndConfig;
		},
		setAuthInfo(authInfo) {
			this.authInfo = authInfo;
		},
		/**
		 * 更新用户信息
		 */
		setUserInfo(userInfo) {
			this.userInfo = userInfo;
			window.pData.globalStore.userInfo = userInfo;
		},
		setHeaderType(headerType) {
			this.headerType = headerType;
		},
		/**
		 * 更新子系统数据。
		 *   - 当只有一个子系统的时候或者访问地址/时，会把第一个子系统作为当前子系统，会调用方法更新当前子系统数据；
		 *   - 如果通过当前子系统别名可以找到当前子系统，调用方法更新当前子系统数据。
		 *
		 */
		setSubSystems(subSystems) {
			if (!subSystems) {
				return;
			}
			this.subSystems = subSystems;
			let curSystem: any = null;
			let curSystemAlias = '';
			if (subSystems.length === 1) {
				curSystem = subSystems[0];
				curSystemAlias = curSystem.alias;
			} else {
				curSystemAlias = this.curSystemAlias || getSubSystemAliasFromLocationPathname(location.pathname) || '';
				if (curSystemAlias) {
					curSystem = this.getSubSystemByAlias(curSystemAlias);
				} else {
					curSystem = subSystems[0];
					curSystemAlias = curSystem.alias;
				}
			}
			if (curSystem && curSystem.alias != this.curSystem?.alias) {
				this.setCurSystem(curSystemAlias, curSystem);
			}
		},
		/**
		 * 更新当前子系统别名以及当前子系统数据。
		 * 将当前子系统别名放到 window.pData.env.curSystemAlias 方便访问。
		 *
		 *
		 */
		setCurSystem(curSystemAlias: string, curSystem?: SubSystem) {
			console.log('setCurSystem.curSystemAlias', curSystemAlias);
			//console.log('location.href', location.href);
			window.pData.pFunc.initPDataCurSystemAlias(curSystemAlias);

			this.curSystemAlias = curSystemAlias;
			this.curSystemName = curSystemAlias;
			if (curSystem) {
				this.curSystem = curSystem;
			} else {
				const subSystem = this.getSubSystemByAlias(curSystemAlias);
				this.curSystem = subSystem;
			}
			this.afterUpdateCurSystem();
		},
		//如果当前子系统没有设置首页，则设置默认首页
		async setHomePage(item) {
			const res = await getMenu({
				systemId: item.id,
			});
			const data = menuUtil.dealDataMenu(res);
			const filterMenu = menuUtil.filterMenu(data);
			const menus = menuUtil.transformToTreeAndLevelFormat(JSON.parse(JSON.stringify(filterMenu)));
			const obj = this.getFirstChild(menus);
			item.homePage = (obj && obj.defaultUrl) || '';
		},
		getFirstChild(tree) {
			for (const item of tree) {
				// 如果当前元素的resourceType等于'menu'，则直接返回该元素
				if (item.resourceType === 'menu') {
					return item;
				}
				// 如果当前元素有子元素，则递归查找子元素
				if (Array.isArray(item.children) && item.children.length > 0) {
					const found = this.getFirstChild(item.children);
					// 如果在子元素中找到了，则返回该元素
					if (found) {
						return found;
					}
				}
			}
			// 如果没有找到符合条件的元素，则返回null
			return null;
		},
		/**
		 * 检查是否需要刷新页面，需要刷新则刷新。
		 * 默认的如果只有一个子系统或地址栏地址不包含当前子系统别名，会跳转到该子系统的首页；
		 * 在或地址栏地址不包含当前子系统别名的情况下（只有一个子系统的情况已删除），
		 * 如果想要访问该地址，需要配置白名单在 power-front-base/frontend/json/production/commonjson.json 文件中。
		 */
		checkAndRefreshRoute() {
			const curSystemAlias = this.curSystemAlias;
			const targeturl = this.curSystem?.homePage;
			const isInNotredirectlist = this.isInNotredirectlist();
			if (
				targeturl &&
				window.pData.env.VITE_NODE_ENV !== 'development' &&
				!isInNotredirectlist &&
				location.href.indexOf(curSystemAlias) === -1
			) {
				//
				const ex = 1 * 60 * 60; // 1h
				Storage.set(REDIRECT_URI_KEY, targeturl, ex);
				this.refreshRoute();
			}
			// if (
			// 	targeturl &&
			// 	window.pData.env.VITE_NODE_ENV !== 'development' &&
			// 	!isInNotredirectlist &&
			// 	(location.href.indexOf(curSystemAlias) === -1 || this.subSystems.length === 1)
			// ) {
			// 	//
			// 	const ex = 1 * 60 * 60; // 1h
			// 	Storage.set(REDIRECT_URI_KEY, targeturl, ex);
			// 	this.refreshRoute();
			// }
		},
		/**
		 * 更新当前子系统之后调用这个方法。
		 *   - 更新favicon和document.title;
		 *   - 注入commonstyle.css;
		 *   - 注入commonjs.js;
		 *   - 更新当前子系统的头部样式；
		 *   - 检查是否需要刷新页面，需要刷新则刷新。
		 *
		 *
		 */
		afterUpdateCurSystem() {
			this.refreshFavicon();
			this.injectCssToDocument();
			this.injectJsToDocument();
			const headerType = getHeaderType();
			if (headerType !== this.headerType) {
				this.setHeaderType(headerType);
			}
			this.checkAndRefreshRoute();
		},
		/**
		 * 获取子系统数据通过子系统别名
		 */
		getSubSystemByAlias(alias: string): SubSystem | undefined {
			const subSystems: SubSystems = this.subSystems;
			const subSystemsArr = subSystems.filter((value) => value.alias === alias);
			const subSystem = subSystemsArr[0];
			if (subSystem) {
				return subSystem;
			} else {
				// ElMessageBox.alert('系统名称不正确，获取系统信息失败!');
				//console.log('系统名称不正确，获取系统信息失败!');
				return;
			}
		},
		refreshFavicon() {
			const logoUrl = getFrontEndConfigItemByCodeAndItemCode(this.frontEndConfig, 'login', 'logo')?.itemValue;
			const title: any =
				getFrontEndConfigItemByCodeAndItemCode(this.frontEndConfig, 'login', 'title')?.itemValue || '\u200E';
			const faviconUrl = logoUrl && getFileUrlById(logoUrl);
			document.title = title;
			console.log('faviconUrl', faviconUrl);
			// const faviconUrl = this.curSystem?.logo || (logoUrl && getFileUrlById(logoUrl));;
			faviconUrl && setDocumentFavicon(faviconUrl);
		},
		injectCssToDocument() {
			const cssUrl = getEnvUrlByUrl(`power-front-base/frontend/css/commonstyle.css`);

			injectCssToDocument(document, cssUrl);
		},
		injectJsToDocument() {
			const cssUrl = getEnvUrlByUrl(`power-front-base/frontend/js/commonjs.js`);
			injectJsToDocument(document, cssUrl);
		},
		/**
		 * 更新 token，并存储在 globalStore、Storage 和 Cookie 中。
		 */
		setToken(data: any, type?: any) {
			if (type === 'Y') {
				const { access_token: accessToken } = data;
				this.token = accessToken ?? '';
				const ex = 7 * 24 * 60 * 60; // 7d
				Storage.set(ACCESS_TOKEN_KEY, this.token, ex);
				window.pData.globalStore.token = this.token;
			} else {
				const { access_token: accessToken, expires_in: expiresIn } = data;
				this.token = accessToken ?? '';
				let ex = 7 * 24 * 60 * 60; // 7d
				if (expiresIn) {
					ex = expiresIn * 60; // expiresIn m
				}
				Storage.set(ACCESS_TOKEN_KEY, this.token, ex);
				window.pData.globalStore.token = this.token;
			}
		},

		/**
		 * 如果有需要则刷新路由。
		 * 这个方法内部区分开发环境和生产环境。
		 * 当只有一个子系统且访问my-systems子系统选择页面的时候直接跳转到唯一子系统的首页。
		 * 如果这个方法已经消耗掉了REDIRECT_URI则返回true，如果需要直接刷新路由，则直接刷新路由；
		 * 否则返回false，由调用者去编写后续逻辑。
		 *
		 */
		refreshRoute() {
			const redirectUri = Storage.get(REDIRECT_URI_KEY, null);
			console.log('redirectUri', redirectUri);

			if (redirectUri) {
				Storage.remove(REDIRECT_URI_KEY);
				let url = redirectUri;
				if (this.subSystems.length === 1 && location.href.indexOf('my-systems') > -1) {
					// 只有一个子系统且想要访问我的系统页面的时候直接跳转到首页
					const redirectUri = this.curSystem?.homePage;

					if (redirectUri) {
						if (redirectUri.indexOf('http') > -1) {
							url = redirectUri;
						} else {
							const arr = redirectUri.split('/');

							if (window.pData.env.VITE_NODE_ENV === 'development') {
								url = redirectUri.substring(arr[1].length + 1);
							} else {
								url = getEnvUrlByUrl(redirectUri);
							}
						}
						location.href = url;
						return true;
					}
					//console.log('即将跳转到子系统首页');
				}
				if (location.href === url) {
					// 是当前地址则不跳转
					return true;
				}

				if (redirectUri.indexOf('http') > -1) {
					url = redirectUri;
				} else {
					const arr = redirectUri.split('/');

					if (window.pData.env.VITE_NODE_ENV === 'development') {
						url = redirectUri.substring(arr[1].length + 1);
					} else {
						url = getEnvUrlByUrl(redirectUri);
					}
				}

				// console.log('路由即将跳转,url', url);

				if (this.headerType === '2') {
					return true;
				}
				location.href = url;
				return true;
			}
			return false;
		},
		/**
		 *
		 * 进入登录页之前会调用这个方法，判断是否需要设置当前地址为登录成功后的跳转地址，如果需要则设置，一般的都会设置。
		 * 目前在登录页面时,不会设置重定向地址其他页面都会设置。
		 */
		setRedirectUriBeforeLogin() {
			const loginUri = window.pData?.env?.loginUri || '/login';
			if (
				location.href.indexOf(loginUri) === -1 &&
				location.href != `${location.origin}/` && // 这一行应该是可以去掉的
				location.href != `${location.origin}${loginUri}` &&
				location.href != `${location.origin}/index`
			) {
				const ex = 1 * 60 * 60; // 1h
				Storage.set(REDIRECT_URI_KEY, location.href, ex);
			}
		},
		async toLogin() {
			// 如果用户没有登录 获取认证信息
			const authInfo = this.authInfo;
			this.setRedirectUriBeforeLogin();

			// 如果是CAS认证
			if (authInfo.authType === AuthTypeEnum.CAS) {
				// 去CAS服务器认证获取 JESSIONID
				await this.toLoginCas();
			} else if (authInfo.authType === AuthTypeEnum.OAUTH2) {
				// 去OATH2服务器认证获取 code和state
				await this.toLoginOauth2();
			} else {
				// AuthTypeEnum.NATIVE
				// AuthTypeEnum.LDAP
				await this.toLoginNative();
			}
		},

		async toLoginNative() {
			// console.log('location.href', location.href);
			const uniDataEnvStr = window.pData.utils.Storage.get('uniDataEnv', '{}');
			const uniDataEnv = JSON.parse(uniDataEnvStr);
			// 退出到移动端登录页面
			if (window.top?.uni?.reLaunch && uniDataEnv?.plus) {
				window.top?.uni?.reLaunch({
					url: '/pages/sys/login/index',
				});
				return;
			}
			const loginUri = window.pData?.env?.loginUri || '/login';
			if (location.href.indexOf(loginUri) === -1) {
				location.href = loginUri;
				throw new Error('正在进入登录页面请稍后...');
			}
		},

		async toLoginCas() {
			// 如果用户没有登录 获取认证信息
			const authInfo = this.authInfo;
			if (location.href.indexOf('cas?service') > -1 || location.href.indexOf('ticket=ST') > -1) {
				//console.log('should not to cas login');
				return;
			} else {
				// 去CAS服务器认证获取 JESSIONID
				// http://192.168.140.56:8443/cas/login?service=https://192.168.140.62:443/auth/redirect?redirect_uri=https://192.168.140.62:443
				// const service = `${location.origin}/auth/redirect?redirect_uri=${location.href}`;
				// const serviceEncode = encodeURIComponent(service);
				location.href = `${authInfo.casServerUrl}`;
				throw new Error('正在进入登录页面请稍后...');
			}
		},

		async toLoginOauth2() {
			const authInfo = this.authInfo;
			if (location.href.indexOf('/auth/oauth2Login?code') > -1) {
				//console.log('should not to oauth2 login');
				return;
			}
			// 如果用户没有登录 获取认证信息
			location.href = `${authInfo.oauth2ServerUrl}`;
			throw new Error('正在进入登录页面请稍后...');
		},

		/**
		 * 判断是否是静默登录，如果是静默登录则进行相关处理。
		 */
		async isLoginSilent() {
			// 静默登录方式二 通过 username, password, targeturl
			if (location.href.indexOf('/auth/silent2') > -1) {
				// 用url中的账号密码或服务器配置文件 /power-front-base/frontend/json/auth-silent2.json 中的账号密码进行登录
				console.log('/auth/silent2');
				const params = param2Obj(location.search);
				const { username, password, targeturl } = params;
				const res = await getAuthSilent2Config();
				const { username: username2, password: password2, targeturl: targeturl2 } = res;
				let tmpUsername = '';
				let tmpPassword = '';
				const tmpTargeturl = targeturl || targeturl2 || '%2F';

				//console.log('other', other);
				if (username && password) {
					tmpUsername = username;
					tmpPassword = password;
				} else {
					tmpUsername = username2;
					tmpPassword = password2;
				}

				await this.login(AuthTypeEnum.NATIVE, {
					userAccount: tmpUsername,
					password: tmpPassword,
				});
				if (tmpTargeturl) {
					let url = decodeURIComponent(tmpTargeturl);
					url = getEnvUrlByUrl(url);
					location.href = url;
				}
				return true;
			}
			// 静默登录方式一 通过 token, targeturl
			if (location.href.indexOf('/auth/authSilent') > -1 || location.href.indexOf('/auth/silent') > -1) {
				//console.log('authSilent');
				const params = param2Obj(location.search);
				const { token, targeturl } = params;
				//console.log('other', other);

				if (token) {
					if (targeturl) {
						const ex = 1 * 60 * 60; // 1h
						Storage.set(REDIRECT_URI_KEY, decodeURIComponent(targeturl), ex);
					}
					this.setToken({ access_token: token });
					await this.handleLoggedIn(true);
					return true;
				}
				return true;
			}

			return false;
		},

		/**
		 * 地址栏包含 /whitelist/ 则页面不需要token即可访问；
		 */
		isInWhitelist() {
			let isWhite = false;
			this.commonjson.whitelist.map((item) => {
				if (location.href.indexOf(item) > -1) {
					isWhite = true;
				}
			});
			if (location.href.indexOf('/whitelist/') > -1 || isWhite) {
				//console.log('isInWhitelist');
				return true;
			}
			return false;
		},

		/**
		 * 地址栏包含 /notredirect/ 则页面可以直接访问，不会默认跳转到当前子系统首页；
		 * 在只有一个子系统或地址栏地址不包含当前子系统别名的情况下，如果想要访问该地址，
		 * 需要配置 notredirect 白名单在 power-front-base/frontend/json/production/commonjson.json 文件中。
		 */
		isInNotredirectlist() {
			let isWhite = false;
			this.commonjson.notredirect.map((item) => {
				if (location.href.indexOf(item) > -1) {
					isWhite = true;
				}
			});
			if (location.href.indexOf('/notredirect/') > -1 || isWhite) {
				//console.log('isInNotredirectlist');
				return true;
			}
			return false;
		},

		/**
		 * 不同的登录方式去获取 token
		 */
		async getToken() {
			const authInfo = this.authInfo;

			// 如果是CAS认证
			if (authInfo.authType === AuthTypeEnum.CAS) {
				const params = param2Obj(location.search);
				if (params.ticket) {
					const res = await getCasLoginInfo(params);
					this.setToken(res.data);
					location.search = '';
				}
			} else if (authInfo.authType === AuthTypeEnum.OAUTH2) {
				const params = param2Obj(location.search);
				if (params.code) {
					const res = await getOauth2LoginInfo(params);
					this.setToken(res.data);
					location.search = '';
				}
			} else {
				//console.log('other');
			}
		},

		/**
		 * 根据是否已登录来进行不同的逻辑处理。
		 * - 如果未登录，则跳转登录页面，目前只支持默认通用登录。
		 * - 如果已登录，则先获取token（单点登录适配需要），然后获取用户信息和子系统数据（获取子系统数据接口地址可以定制化）。
		 *   - refreshRoute 如果需要直接刷新路由则直接刷新路由，如果不需要直接刷新路由则，返回false。由调用者去编写后续逻辑。
		 *
		 */
		async handleLoggedIn(loggedIn) {
			// 判断用户是否登录
			if (loggedIn) {
				// 已登录
				// 如果用户已经登录 那么获取token
				await this.getToken();
				//const token = Storage.get(ACCESS_TOKEN_KEY, null);
				//console.log('token', token);
				// 如果用户已经登录 获取用户信息并保存
				const [userInfo, subSystems]: any = await requestDataAfterLogin({
					getSubSystemParams: {
						url: this.urlGetSubSystem,
						isMobile: this.isMobile,
					},
				});
				//console.log('userInfo', userInfo);
				this.setUserInfo(userInfo);
				// 如果子系统首页地址为空，则根据子系统菜单权限获取第一个子系统首页地址,循环调接口，如果太多页面可能会卡顿
				for (const subSystem of subSystems) {
					if (!subSystem.homePage) {
						await this.setHomePage(subSystem);
					}
				}
				this.setSubSystems(subSystems);
				const userAccount = userInfo.user.account;
				Storage.set('userAccount', userAccount);
				// const themeStyle = Storage.get('themeStyle', 'blue');
				// const themeStyleNew = userInfo.user.themeStyle;
				// if (themeStyle !== themeStyleNew) {
				// 	setDocumentTheme(themeStyleNew);
				// 	Storage.set('themeStyle', themeStyleNew);
				// }
				const refreshedRoute = this.refreshRoute();
				// 如果已经刷新了路由那么这个方法返回后不需要再次刷新路由
				return {
					refreshedRoute,
				};
			} else {
				// 未登录 则登录
				await this.toLogin();
			}
		},

		/** 初始化数据从本地获取 */
		async initDataFromLocal() {
			try {
				const params = param2Obj(location.search);
				if (params.hidePcLayoutHeader === '1' || params.plmh === '0') {
					this.visible.pcLayoutHeader = false;
				} else if (params.hidePcLayoutHeader === '0' || params.plmh === '1') {
					this.visible.pcLayoutHeader = true;
				}
				if (params.hidePcLayoutMenu === '1' || params.plmm === '0') {
					this.visible.pcLayoutMenu = false;
				} else if (params.hidePcLayoutMenu === '0' || params.plmm === '1') {
					this.visible.pcLayoutMenu = true;
				}
				if (params.plmhm === '0') {
					this.visible.pcLayoutHeader = false;
					this.visible.pcLayoutMenu = false;
				} else if (params.plmhm === '1') {
					this.visible.pcLayoutMenu = true;
					this.visible.pcLayoutMenu = true;
				}
				const themeStyle = Storage.get('themeStyle', 'blue');
				setDocumentTheme(themeStyle);
				window.pData.globalStore = this;
				return true;
			} catch (error) {
				console.log('error', error);
				return Promise.reject(error);
			}
		},

		/** 初始化数据从服务器获取 */
		async initDataFromServer() {
			// 从服务器获取登录前需要获取到的数据，包括前端配置数据和是否登录。
			const [frontEndConfig, loggedIn] = await requestDataBeforeLogin();
			const resetPasswordStore = useResetPasswordStore();
			console.log(loggedIn, 'ppp');
			if (Object.prototype.hasOwnProperty.call(loggedIn, 'hasPwd') && loggedIn.hasPwd === false) {
				resetPasswordStore.showDialog();
			} else {
				resetPasswordStore.hideDialog();
			}
			if (loggedIn?.access_token) {
				this.setToken(loggedIn, 'Y');
			}
			//console.log('frontEndConfig', frontEndConfig);
			this.setFrontEndConfig(frontEndConfig);
			if (await this.isLoginSilent()) {
				return true;
			}
			await this.handleLoggedIn(loggedIn?.status);
			return true;
		},
		/** 登录 */
		async login(authType: string, params: LoginParams) {
			try {
				const { data }: any = await login(authType, params);
				//console.log('login.data', data);
				this.setToken(data);
				return await this.handleLoggedIn(true);
			} catch (error) {
				console.log('error', error);
				return Promise.reject(error);
			}
		},
		// 退出登录
		async logOut(callback) {
			try {
				await logout();
				this.onLogout();
				if (callback) {
					await callback();
				} else {
					location.href = window.pData?.env?.loginUri || '/login';
				}
				return true;
			} catch (error) {
				//console.log('error', error);
				return Promise.reject(error);
			}
		},
		/**
		 * 清除 token、清除 localStorage、清除 sessionStorage、清除 cookie。
		 */
		onLogout() {
			this.token = '';
			const themeStyle = Storage.get('themeStyle', 'blue');
			localStorage.clear();
			sessionStorage.clear();
			// 清除cookies
			document.cookie
				.split(';')
				.forEach(
					(cookie) =>
						(document.cookie = cookie
							.replace(/^ +/, '')
							.replace(/=.*/, `=;expires=${new Date(0).toUTCString()};path=/`)),
				);
			setDocumentTheme(themeStyle);
		},
		/** 初始化基础数据 */
		async initData() {
			try {
				await this.initDataFromLocal();
				findTreeDataWithNoRoot();
				try {
					const res = await getCommonjson();
					if (res) {
						this.setCommonjson(res);
					}
				} catch (error) {
					console.log('commonjson 初始化失败！');
				}
				const isInWhitelist = this.isInWhitelist();
				if (isInWhitelist) {
					return true;
				} else {
					await this.initDataFromServer();
				}
				// 加载完成
				return true;
			} catch (error) {
				console.log('error', error);
				return Promise.reject(error);
			}
			return false;
		},
	},
});

// 在组件setup函数外使用
export function useGlobalStoreWithOut(): any {
	return useGlobalStore(store);
}
